import {
  AUTHENTICATION_REFRESHER,
  AuthenticationRefresher,
  AuthenticationService,
  IdentityValidation,
  ValidAuthentication,
  ValidAuthenticationResponse
} from '@/app/platform/platform-core/user/services/types';
import { Inject, Injectable } from '@angular/core';
import { PlatformUserAccountService } from '@data/_generated-api/platform-api/services/platform-user-account.service';
import { AsyncResult } from '@common/result/asyncResult';
import { err, InvalidResult, ok } from '@common/result/Result';
import { PlatformUser } from '@/app/platform/platform-core/user/models/platform-user';
import {
  AUTHENTICATION_OBSERVABLE_FACTORY,
  AuthenticationObservableFactory
} from '@/app/platform/platform-core/user/factories/types';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import Morphism from 'morphism';
import { HttpClient } from '@angular/common/http';
import { PlatformApiResultOfUserInfoDto } from '@data/_generated-api/platform-api/models/platform-api-result-of-user-info-dto';
import { PlatformApiResultOfBoolean } from '@data/_generated-api/platform-api/models/platform-api-result-of-boolean';
import { UpamErrorMapper } from '@data/_generated-api/error-mappers/upam-error-mapper';
import { PlatformApiResultOfAanmeldValidationResult } from '@data/_generated-api/platform-api/models/platform-api-result-of-aanmeld-validation-result';
import { NavigationItemDataEnricher } from '@/app/platform/platform-shared/enrichers/navigation-item-data-enricher';
import { Credentials } from '@/app/platform/platform-shared/models/credentials';
import { environment } from '@/environments/environment';
import { currentTab } from '@/app/current-tab';

// IMPORTANT: the autogenerated clients are not used in some cases.
// This is because we need the 'withCredentials' flag when we need to save/send the refresh token cookie

@Injectable({
  providedIn: 'root'
})
export class AuthenticationApiService implements AuthenticationService {
  constructor(
    private readonly userService: PlatformUserAccountService,
    private readonly navigationItemEnricher: NavigationItemDataEnricher,
    @Inject(AUTHENTICATION_OBSERVABLE_FACTORY)
    private readonly authenticationObservableFactory: AuthenticationObservableFactory,
    private readonly http: HttpClient,
    private readonly upamErrorMapper: UpamErrorMapper,
    @Inject(AUTHENTICATION_REFRESHER)
    private readonly authenticationRefresher: AuthenticationRefresher
  ) {}

  async authenticate(authenticationToken: string): AsyncResult<ValidAuthenticationResponse> {
    const url = environment.apiPaths.egituraPlatformApi + '/api/userpresence/action/desktopmanueelaanmelden';
    const result = await this.http
      .post<PlatformApiResultOfUserInfoDto>(
        url,
        {
          authOnlyToken: authenticationToken
        },
        {
          withCredentials: true
        }
      )
      .toPromise();

    if (!result.isValid) {
      const errors = this.upamErrorMapper.map(result.errors);
      return err(errors);
    }

    if (currentTab.getUseMultipleTabs()) {
      currentTab.makeLeading();
      currentTab.saveAuth(result);
      currentTab.notify('login');
    }
    const authentication = this.mapResponseToAuthenticationResponse(result);

    const authenticationResponse: ValidAuthenticationResponse = {
      refresher$: this.authenticationObservableFactory.create(),
      authentication
    };

    return ok(authenticationResponse);
  }

  private mapResponseToAuthenticationResponse(
    result: PlatformApiResultOfUserInfoDto
  ): ValidAuthentication {
    const apps = Morphism.map(UserEgiApp, result.value.apps);
    const user = Morphism.map(PlatformUser, result.value);
    apps.forEach(a => this.navigationItemEnricher.enrichWithEgiPages(a));

    return {
      apps,
      user,
      jwtToken: result.value.jwtToken
    };
  }

  async validateCredentials(credentials: Credentials): AsyncResult<IdentityValidation> {
    const result = await this.userService
      .postUseraccountsActionValidateemailandpassword({
        body: {
          email: credentials.email,
          password: credentials.password
        }
      })
      .toPromise();

    return await this.extractAndHydrateResult(result);
  }

  async logout(): AsyncResult<boolean> {
    const url = `${environment.apiPaths.egituraPlatformApi}${environment.auth.logoutRoute}`;
    const result = await this.http
      .post<PlatformApiResultOfBoolean>(
        url,
        {},
        {
          withCredentials: true
        }
      )
      .toPromise();

    if (!result.isValid) {
      const errors = this.upamErrorMapper.map(result.errors);
      return err(errors);
    }

    currentTab.clearAuth();
    currentTab.notify('logout');

    return ok(true);
  }


  async loadSavedAuthentication(): AsyncResult<ValidAuthenticationResponse> {
    let result: Awaited<AsyncResult<ValidAuthentication>>;
    try {
      result = await this.authenticationRefresher.refresh();
    } catch {
      // Bv. API onbereikbaar tijdens het opstarten, ook na de herhaalpogingen van de
      // refresher: geen sessie, maar de bootstrap mag daar niet op breken - de gebruiker
      // komt gewoon op de loginpagina uit.
      return err();
    }
    if (result instanceof InvalidResult) {
      return result;
    }
    const authentication = result.value;

    const refresher$ = this.authenticationObservableFactory.create();

    const response: ValidAuthenticationResponse = {
      authentication,
      refresher$
    };

    return ok(response);
  }

  private async extractAndHydrateResult(
    result: PlatformApiResultOfAanmeldValidationResult
  ): AsyncResult<IdentityValidation> {
    if (!result.isValid) {
      const errors = this.upamErrorMapper.map(result.errors);
      return err(errors);
    }

    const user = Morphism.map(PlatformUser, result.value);
    const validation: IdentityValidation = [user, result.value.authenticationOnlyToken];
    return ok(validation);
  }
}
